import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { CheckCircle2, Printer } from "lucide-react"
import Modal from "react-bootstrap/Modal"
import Spinner from "react-bootstrap/Spinner"
import generatePDF, { Margin } from "react-to-pdf"
import Badge from "../common/Badge.jsx"
import Button from "../common/Button.jsx"
import Toast from "../common/Toast.jsx"
import V2PageContainer from "../common/V2PageContainer.jsx"
import { getManifestStatusBadgeStyle } from "../utils/manifestStatusBadge.js"
import ScrollToTopButton from "../common/ScrollToTopButton.jsx"
import useIsMobileViewport from "../hooks/useIsMobileViewport.js"
import useNotImplementedToast from "../hooks/useNotImplementedToast.js"
import { refetchRemainingMs } from "../utils/loading.js"
import { getLocationShortLabel } from "../utils/locations.js"
import OrdersDesktopTable from "../orders/OrdersDesktopTable.jsx"
import OrdersMobileList from "../orders/OrdersMobileList.jsx"
import { BoxLabels, BoxLabelsProgressModal } from "../box-labels/index.js"
import { getBootstrapCssDataUrl } from "../utils/pdfBootstrapCss.js"

const MANIFEST_ORDERS_PAGE_SIZE = 100
// Controls the max width of each of the two detail columns on desktop.
const MANIFEST_DETAILS_COLUMN_MAX_WIDTH_PX = 300
const MANIFEST_DETAILS_PAIR_MAX_WIDTH_PX = MANIFEST_DETAILS_COLUMN_MAX_WIDTH_PX
const MANIFEST_DETAILS_PAIR_STYLE = {
    width: `min(100%, ${MANIFEST_DETAILS_PAIR_MAX_WIDTH_PX}px)`,
    marginRight: "auto",
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    columnGap: "16px",
    alignItems: "baseline",
}
const MANIFEST_DETAILS_PAIR_VALUE_STYLE = {
    justifySelf: "end",
    textAlign: "right",
}

/**
 * @param {{
 *   app: any,
 *   api: any,
 *   helpers?: {
 *     formatStatus?: (status: any) => string,
 *     formatManifestStatus?: (status: any) => string,
 *     getOriginalLocation?: (order: any, locations: any[]) => any,
 *     lastNameCommaFirstName?: (address: any) => string,
 *     isOverseas?: (order: any) => boolean,
 *   },
 *   manifestDetailsFields?: { key: string, label: string }[],
 *   showAdditionalInformation?: boolean,
 *   showLabelDescriptionEditing?: boolean,
 *   showPrintManifestButton?: boolean,
 *   showPrintLabelButton?: boolean,
 *   showPrintBoxLabelsButton?: boolean,
 *   injectBootstrap?: boolean,
 *   enableLodgeStatusButtons?: boolean,
 *   fetchOrdersCallback?: (args: {
 *     manifestId: number,
 *     page: number,
 *     perPage: number,
 *     sortBy: string,
 *     sortDirection: "asc" | "desc",
 *   }) => Promise<any>,
 *   orderColumns?: Array<{
 *     key: string,
 *     label: any,
 *     sortable?: boolean,
 *     preferredWidth?: number,
 *     minWidth?: number,
 *     cellType?: "text" | "status" | "custom",
 *     value?: (order: any) => any,
 *     render?: (order: any) => any,
 *     headerClassName?: string,
 *     headerStyle?: any,
 *     cellClassName?: string,
 *     cellStyle?: any,
 *   }>,
 * }} props
 */
export default function ManifestPage({
    app,
    api,
    helpers,
    manifestDetailsFields = undefined,
    showAdditionalInformation = true,
    showLabelDescriptionEditing = true,
    showPrintManifestButton = true,
    showPrintLabelButton = true,
    showPrintBoxLabelsButton = true,
    injectBootstrap = false,
    enableLodgeStatusButtons = false,
    disableManifestSendWhenOffline = false,
    disableManifestWritesWhenOffline = false,
    fetchOrdersCallback = undefined,
    orderColumns = undefined,
}) {
    const { toastProps, showNotImplementedToast } = useNotImplementedToast()
    const { manifestId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const navigate = useNavigate()
    const isMobile = useIsMobileViewport()
    const [isLgUp, setIsLgUp] = useState(
        typeof window !== "undefined" ? window.innerWidth >= 992 : true
    )
    const [manifest, setManifest] = useState(null)
    const [orders, setOrders] = useState([])
    const [rawOrders, setRawOrders] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [refetchToken, setRefetchToken] = useState(0)
    const [showBoxLabelsModal, setShowBoxLabelsModal] = useState(false)
    const [renderBoxLabelsPdf, setRenderBoxLabelsPdf] = useState(false)
    const [ordersToPrintLabels, setOrdersToPrintLabels] = useState([])
    const [boxLabelProgress, setBoxLabelProgress] = useState(0)
    const cancelBoxLabelsRef = React.useRef(false)
    const requestIdRef = React.useRef(0)
    const hasLoadedOnceRef = React.useRef(false)
    const refetchLoaderInFlightRef = React.useRef(0)
    const [showRefetchLoader, setShowRefetchLoader] = useState(false)

    const [actionToastMessage, setActionToastMessage] = useState("")
    const [showActionToast, setShowActionToast] = useState(false)
    const [actionToastType, setActionToastType] = useState(
        /** @type {"info" | "warning" | "error" | "success"} */("error")
    )
    const [actionToastTitle, setActionToastTitle] = useState("")
    const [actionToastAutoDismissMs, setActionToastAutoDismissMs] = useState(
        /** @type {number | null} */(2600)
    )
    const [actionToastVersion, setActionToastVersion] = useState(0)

    const [showManifestActionModal, setShowManifestActionModal] = useState(false)
    const [manifestActionModal, setManifestActionModal] = useState(
        /** @type {null | { title: string, body: string, primaryText: string, onPrimary: () => Promise<void> }} */(null)
    )
    const [manifestActionLoading, setManifestActionLoading] = useState(false)
    const [renderManifestPdf, setRenderManifestPdf] = useState(false)
    const [bootstrapCssHref, setBootstrapCssHref] = useState(/** @type {null | string} */(null))

    const formatStatus = helpers?.formatStatus ?? ((x) => String(x ?? ""))
    const formatManifestStatus = helpers?.formatManifestStatus ?? formatStatus
    const getOriginalLocation = helpers?.getOriginalLocation ?? (() => null)
    const lastNameCommaFirstName = helpers?.lastNameCommaFirstName ?? (() => "")
    const isOverseas = helpers?.isOverseas ?? ((order) => {
        const country = String(order?.ship_to?.country ?? order?.bill_to?.country ?? "").trim().toLowerCase()
        return Boolean(country && country !== "usa" && country !== "us" && country !== "united states" && country !== "united states of america")
    })

    const manifestSendDisabledBecauseOffline = Boolean(disableManifestSendWhenOffline && app?.connected === false)
    const manifestSendDisabledTooltip = "Cannot send manifests while offline."
    const manifestWriteDisabledBecauseOffline = Boolean(disableManifestWritesWhenOffline && app?.connected === false)
    const manifestWriteDisabledTooltip = "Cannot edit manifests while offline."

    useEffect(() => {
        let cancelled = false
        if (!injectBootstrap) {
            setBootstrapCssHref(null)
            return () => {
                cancelled = true
            }
        }
        getBootstrapCssDataUrl()
            .then((href) => {
                if (!cancelled) {
                    setBootstrapCssHref(href)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setBootstrapCssHref(null)
                }
            })
        return () => {
            cancelled = true
        }
    }, [injectBootstrap])

    useEffect(() => {
        function onResize() {
            setIsLgUp(window.innerWidth >= 992)
        }

        window.addEventListener("resize", onResize)
        return () => window.removeEventListener("resize", onResize)
    }, [])

    const sortByParam = searchParams.get("sort_by")
    const sortDirectionParam = searchParams.get("sort_dir")

    function getSortBy() {
        return sortByParam ?? "orderDate"
    }

    function getSortDirection() {
        return sortDirectionParam === "asc" ? "asc" : "desc"
    }

    function getSortField(columnKey) {
        switch (columnKey) {
            case "master":
                return "master"
            case "status":
                return "status"
            case "firstName":
                return "ship_first_name"
            case "lastName":
                return "ship_last_name"
            case "boxes":
                return "total_boxes"
            case "sjNum":
                return "st_jeans_num"
            case "orderDate":
            default:
                return "ordered_at"
        }
    }

    function mapManifestOrder(order, locations) {
        const store = getOriginalLocation(order, locations)
        const pickUpLocation = locations.find(
            (x) => x.id === order.pick_up_location_id
        )
        const shipTo = pickUpLocation ?
            `P/U: ${pickUpLocation.pick_up_name || pickUpLocation.name}` :
            lastNameCommaFirstName(order.ship_to)

        return {
            id: order.id,
            location: store?.name ?? "",
            locationShort: (getLocationShortLabel(store) || store?.name) ?? "",
            master: order.master,
            status: formatStatus(order.status),
            firstName: order.bill_same_as_ship ?
                (order.ship_to?.first_name ?? "") :
                (order.bill_to?.first_name ?? ""),
            lastName: order.bill_same_as_ship ?
                (order.ship_to?.last_name ?? "") :
                (order.bill_to?.last_name ?? ""),
            fishTag: order.fish_tag ?? "",
            tripCode: order.trip_code ?? "",
            sjNum: order.st_jeans_num ?? "",
            shipTo,
            boxes: order.total_boxes ?? "",
            orderDate: order.ordered_at?.split("T")?.[0] ?? "",
        }
    }

    useEffect(() => {
        if (!manifestId || !app?.loaded) {
            return
        }

        const isRefetch = hasLoadedOnceRef.current
        const fetchStartedAt = Date.now()

        setIsLoading(true)
        function completeRefetchLoaderSlot() {
            refetchLoaderInFlightRef.current = Math.max(0, refetchLoaderInFlightRef.current - 1)
            if (refetchLoaderInFlightRef.current === 0) {
                setShowRefetchLoader(false)
            }
        }
        if (isRefetch) {
            refetchLoaderInFlightRef.current += 1
            setShowRefetchLoader(true)
        }
        const requestId = ++requestIdRef.current
        const locations = /** @type {any[]} */ (app?.locations ?? [])
        const filters = [`{manifest_id = ${manifestId}}`]

        const fetchOrders = fetchOrdersCallback ? (
            fetchOrdersCallback({
                manifestId: Number(manifestId),
                page: 1,
                perPage: MANIFEST_ORDERS_PAGE_SIZE,
                sortBy: getSortField(getSortBy()),
                sortDirection: getSortDirection(),
            })
        ) : (
            api.getOrders(
                1,
                MANIFEST_ORDERS_PAGE_SIZE,
                getSortField(getSortBy()),
                getSortDirection(),
                "",
                filters
            )
        )

        Promise.all([
            api.getManifest(Number(manifestId)),
            fetchOrders,
        ])
            .then(([manifestBody, ordersBody]) => {
                if (requestId !== requestIdRef.current) {
                    if (isRefetch) {
                        completeRefetchLoaderSlot()
                    }
                    return
                }
                const nextManifest = manifestBody ?? null
                const nextRawOrders = ordersBody?.data ?? []
                const nextOrders = nextRawOrders.map((order) => (
                    mapManifestOrder(order, locations)
                ))
                if (!isRefetch) {
                    setManifest(nextManifest)
                    setRawOrders(nextRawOrders)
                    setOrders(nextOrders)
                    return
                }
                const delay = refetchRemainingMs(fetchStartedAt)
                window.setTimeout(() => {
                    if (requestId !== requestIdRef.current) {
                        completeRefetchLoaderSlot()
                        return
                    }
                    setManifest(nextManifest)
                    setRawOrders(nextRawOrders)
                    setOrders(nextOrders)
                    setIsLoading(false)
                    completeRefetchLoaderSlot()
                }, delay)
            })
            .catch((error) => {
                if (requestId !== requestIdRef.current) {
                    if (isRefetch) {
                        completeRefetchLoaderSlot()
                    }
                    return
                }
                console.error("Failed to load v2 manifest detail:", error)
                if (!isRefetch) {
                    return
                }
                const delay = refetchRemainingMs(fetchStartedAt)
                window.setTimeout(() => {
                    if (requestId !== requestIdRef.current) {
                        completeRefetchLoaderSlot()
                        return
                    }
                    setIsLoading(false)
                    completeRefetchLoaderSlot()
                }, delay)
            })
            .finally(() => {
                if (requestId !== requestIdRef.current) {
                    return
                }
                if (isRefetch) {
                    return
                }
                setIsLoading(false)
                if (!hasLoadedOnceRef.current) {
                    hasLoadedOnceRef.current = true
                }
            })
    }, [
        manifestId,
        app?.loaded,
        app?.locations,
        sortByParam,
        sortDirectionParam,
        refetchToken,
    ])

    useEffect(() => {
        if (!renderManifestPdf) {
            return
        }
        const options = {
            method: "open",
            page: {
                margin: Margin.SMALL,
                format: "letter",
                orientation: "portrait",
            },
        }
        const getTargetElement = () => document.getElementById("manifest-pdf")
        generatePDF(getTargetElement, options)
            .catch((e) => {
                console.error("Failed to open manifest PDF:", e)
                showActionErrorToast("Error generating manifest PDF.")
            })
            .finally(() => setRenderManifestPdf(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderManifestPdf])

    function showActionErrorToast(message) {
        setActionToastType("error")
        setActionToastTitle("Error")
        setActionToastAutoDismissMs(null)
        setActionToastMessage(message)
        setActionToastVersion((x) => x + 1)
        setShowActionToast(true)
    }

    function showActionSuccessToast(message) {
        setActionToastType("success")
        setActionToastTitle("Success")
        setActionToastAutoDismissMs(2600)
        setActionToastMessage(message)
        setActionToastVersion((x) => x + 1)
        setShowActionToast(true)
    }

    function ensureOnlineOrToast() {
        if (app?.connected) {
            return true
        }
        setActionToastType("warning")
        setActionToastTitle("Offline")
        setActionToastAutoDismissMs(2600)
        setActionToastMessage("Cannot edit manifests while offline")
        setActionToastVersion((x) => x + 1)
        setShowActionToast(true)
        return false
    }

    function getManifestLocation() {
        const id = manifest?.location_id
        if (!id) {
            return null
        }
        return (app?.locations ?? []).find((x) => x.id === id) ?? null
    }

    function openActionModal(cfg) {
        setManifestActionModal(cfg)
        setManifestActionLoading(false)
        setShowManifestActionModal(true)
    }

    async function refreshManifest() {
        if (!manifestId) {
            return
        }
        const fresh = await api.getManifest(Number(manifestId))
        setManifest(fresh ?? null)
    }

    async function onCloseManifestClick() {
        if (!ensureOnlineOrToast()) return
        try {
            const res = await api.closeManifest(Number(manifestId))
            setManifest(res?.data ?? res ?? null)
        } catch (e) {
            console.error(e)
            showActionErrorToast("Error closing manifest")
        }
    }

    async function onReopenManifestClick() {
        if (!ensureOnlineOrToast()) return
        try {
            const res = await api.openManifest(Number(manifestId))
            setManifest(res?.data ?? res ?? null)
        } catch (e) {
            console.error(e)
            showActionErrorToast("Error reopening manifest")
        }
    }

    function buildManifestPdfBlob() {
        const options = {
            method: "build",
            page: {
                margin: Margin.SMALL,
                format: "letter",
                orientation: "portrait",
            },
        }
        const getTargetElement = () => document.getElementById("manifest-pdf")
        return generatePDF(getTargetElement, options).then((jsPdf) => jsPdf.output("blob"))
    }

    async function shipManifestAndUpdate() {
        const location = getManifestLocation()
        const lodgeCode = location?.lodge_code
        if (!lodgeCode) {
            throw new Error("Missing lodge code for manifest location.")
        }
        const blob = await buildManifestPdfBlob()
        const res = await api.shipManifest(Number(manifestId), lodgeCode, manifest?.manifest_num, blob)
        setManifest(res?.data ?? res ?? null)
        await refreshManifest()
    }

    function refetchOrdersList() {
        setRefetchToken((x) => x + 1)
    }

    async function resendManifestEmail() {
        const location = getManifestLocation()
        const lodgeCode = location?.lodge_code
        if (!lodgeCode) {
            throw new Error("Missing lodge code for manifest location.")
        }
        const blob = await buildManifestPdfBlob()
        await api.emailManifest(Number(manifestId), lodgeCode, manifest?.manifest_num, blob)
    }

    useEffect(() => {
        if (!showBoxLabelsModal) {
            setRenderBoxLabelsPdf(false)
            setOrdersToPrintLabels([])
            setBoxLabelProgress(0)
        }
    }, [showBoxLabelsModal])

    useEffect(() => {
        if (!showManifestActionModal) {
            setManifestActionModal(null)
            setManifestActionLoading(false)
        }
    }, [showManifestActionModal])

    useEffect(() => {
        if (!showBoxLabelsModal) {
            return
        }
        if (boxLabelProgress !== 100) {
            return
        }
        const t = window.setTimeout(() => {
            setShowBoxLabelsModal(false)
        }, 500)
        return () => window.clearTimeout(t)
    }, [boxLabelProgress, showBoxLabelsModal])

    const manifestDetails = useMemo(() => {
        if (!manifest) {
            return null
        }
        const location = (app?.locations ?? []).find(
            (entry) => entry.id === manifest.location_id
        )
        return {
            number: manifest.manifest_num,
            rawStatus: manifest.status,
            status: formatManifestStatus(manifest.status),
            depot: location?.name ?? "",
            createdDate: manifest.created_at?.split("T")?.[0] ?? "—",
            transferredDate: manifest.shipped_at?.split("T")?.[0] ?? "—",
            receivedDate: manifest.received_at?.split("T")?.[0] ?? "—",
            closedDate: manifest.closed_at?.split("T")?.[0] ?? "—",
            labelDescription: manifest.label_description || "—",
            syncedToMinotaur: Boolean(manifest.synced_at),
            vvbnReference: manifest.vvbn_reference || "—",
            currentLocation: manifest.minotaur_location || "—",
        }
    }, [manifest, app?.locations, formatManifestStatus])

    function handleSort(columnKey) {
        const nextParams = new URLSearchParams(searchParams)
        const isCurrentSort = getSortBy() === columnKey
        const nextDirection = (
            isCurrentSort && getSortDirection() === "desc" ? "asc" : "desc"
        )
        nextParams.set("sort_by", columnKey)
        nextParams.set("sort_dir", nextDirection)
        setSearchParams(nextParams)
    }

    function handleOrderClick(order) {
        navigate(`/v2/orders/${order.id}/order`)
    }

    if (isLoading && !manifestDetails) {
        return (
            <V2PageContainer className="py-4 py-md-5">
                <div className="bg-white rounded p-3 text-secondary">
                    Loading manifest...
                </div>
            </V2PageContainer>
        )
    }

    if (!manifestDetails) {
        return (
            <V2PageContainer className="py-4 py-md-5">
                <div className="bg-white rounded p-3 text-secondary">
                    Manifest not found.
                </div>
            </V2PageContainer>
        )
    }

    const rawManifestStatus = String(manifest?.status ?? "").toLowerCase()
    const showManifestPrintActions = rawManifestStatus !== "open"
    const detailsFields = manifestDetailsFields ?? [
        { key: "depot", label: "Depot" },
        { key: "createdDate", label: "Created Date" },
        { key: "transferredDate", label: "Transferred Date" },
        { key: "receivedDate", label: "Received Date" },
    ]

    async function fetchAllManifestOrdersRaw() {
        const id = Number(manifestId)
        if (!id || !Number.isFinite(id)) {
            return []
        }
        const perPage = 200
        const maxPages = 50 // hard safety limit
        const sortBy = getSortField(getSortBy())
        const sortDirection = getSortDirection()

        const all = []
        for (let page = 1; page <= maxPages; page++) {
            const body = fetchOrdersCallback ? await fetchOrdersCallback({
                manifestId: id,
                page,
                perPage,
                sortBy,
                sortDirection,
            }) : await api.getOrders(page, perPage, sortBy, sortDirection, "", [`{manifest_id = ${id}}`])
            const items = body?.data ?? []
            all.push(...items)
            if (items.length < perPage) {
                break
            }
        }
        return all
    }

    function onPrintBoxLabelsClick() {
        if (!manifestDetails) {
            return
        }
        setBoxLabelProgress(0)
        setOrdersToPrintLabels([])
        cancelBoxLabelsRef.current = false
        setShowBoxLabelsModal(true)
        Promise.resolve()
            .then(async () => {
                let ordersForLabels = rawOrders
                if (rawOrders.length >= MANIFEST_ORDERS_PAGE_SIZE) {
                    ordersForLabels = await fetchAllManifestOrdersRaw()
                }
                if (!ordersForLabels.length) {
                    throw new Error("No orders found on this manifest.")
                }
                setOrdersToPrintLabels(ordersForLabels.filter(Boolean))
                window.setTimeout(() => setRenderBoxLabelsPdf(true), 200)
            })
            .catch((e) => {
                console.error("Failed to print manifest box labels:", e)
                showNotImplementedToast(e?.message ?? "Failed to generate box labels.")
                setShowBoxLabelsModal(false)
            })
    }

    return (
        <V2PageContainer className="py-4 py-md-5">
            <Toast {...toastProps} />
            <Toast
                key={actionToastVersion}
                show={showActionToast}
                type={actionToastType}
                title={actionToastTitle}
                message={actionToastMessage}
                autoDismissMs={actionToastAutoDismissMs}
                onClose={() => {
                    setShowActionToast(false)
                    setActionToastMessage("")
                }}
            />
            <BoxLabels
                orders={ordersToPrintLabels}
                locations={app?.locations ?? []}
                openPdf={renderBoxLabelsPdf}
                setOpenPdf={setRenderBoxLabelsPdf}
                progress={boxLabelProgress}
                setProgress={setBoxLabelProgress}
                helpers={helpers}
                cancelRef={cancelBoxLabelsRef}
                app={app}
            />
            <BoxLabelsProgressModal
                show={showBoxLabelsModal}
                progress={boxLabelProgress}
                onCancel={() => {
                    cancelBoxLabelsRef.current = true
                    setRenderBoxLabelsPdf(false)
                    setShowBoxLabelsModal(false)
                }}
            />
            <Modal
                show={showManifestActionModal}
                onHide={() => (manifestActionLoading ? null : setShowManifestActionModal(false))}
                centered
            >
                <Modal.Header closeButton={!manifestActionLoading}>
                    <Modal.Title>{manifestActionModal?.title ?? ""}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{manifestActionModal?.body ?? ""}</Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        disabled={manifestActionLoading}
                        onClick={() => setShowManifestActionModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        disabled={manifestActionLoading || !manifestActionModal}
                        onClick={async () => {
                            if (!manifestActionModal) return
                            setManifestActionLoading(true)
                            try {
                                await manifestActionModal.onPrimary()
                                setShowManifestActionModal(false)
                            } catch (e) {
                                console.error(e)
                                showActionErrorToast(e?.message ?? "Action failed")
                            } finally {
                                setManifestActionLoading(false)
                            }
                        }}
                    >
                        {manifestActionLoading ? <Spinner size="sm" /> : (manifestActionModal?.primaryText ?? "Continue")}
                    </Button>
                </Modal.Footer>
            </Modal>
            <div
                id="manifest-pdf"
                style={{ position: "fixed", left: "-3000px", width: "800px", top: 0 }}
            >
                {injectBootstrap && bootstrapCssHref ? <link rel="stylesheet" href={bootstrapCssHref} /> : null}
                <div className="d-flex justify-content-between align-items-end mt-3 mb-2">
                    <div className="fw-bold" style={{ fontSize: "32px" }}>
                        {`Manifest: #${manifest?.manifest_num ?? ""}`}
                    </div>
                    <div className="text-end" style={{ fontSize: "18px" }}>
                        <div>{`Closed Date: ${manifest?.closed_at ? String(manifest.closed_at).split("T")[0] : ""}`}</div>
                        <div>{`Depot: ${getManifestLocation()?.name ?? ""}`}</div>
                    </div>
                </div>
                <table className="table table-sm">
                    <thead>
                        <tr>
                            <th style={{ width: "140px" }}>Master #</th>
                            <th>Name</th>
                            <th style={{ width: "110px" }}>Overseas</th>
                            <th style={{ width: "120px" }}>Ship Only</th>
                            <th style={{ width: "80px" }}>Boxes</th>
                            <th style={{ width: "90px" }}>Weight</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(rawOrders ?? []).map((o) => {
                            const last = o?.bill_same_as_ship ? o?.ship_to?.last_name : o?.bill_to?.last_name
                            const first = o?.bill_same_as_ship ? o?.ship_to?.first_name : o?.bill_to?.first_name
                            const name = [last, first].filter(Boolean).join(", ")
                            const shipOnlyLabel = o?.ship_only ? "Ship Only" : (o?.hold_for_pick_up ? "Hold for P/U" : "")
                            return (
                                <tr key={o?.id ?? o?.master}>
                                    <td>{o?.master ?? ""}</td>
                                    <td>{name}</td>
                                    <td>{isOverseas(o) ? "OVERSEAS" : ""}</td>
                                    <td>{shipOnlyLabel}</td>
                                    <td>{o?.total_boxes ?? ""}</td>
                                    <td>{o?.total_weight_lb != null ? `${o.total_weight_lb} lb` : ""}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            <div className="mb-3 mb-lg-4">
                <h1 className="fs-2 fw-bold text-dark mb-0">
                    Manifest: #{manifestDetails.number}
                </h1>
            </div>

            <div
                className={`${isLgUp ? "position-sticky top-0" : ""} card rounded shadow-sm border mb-4 p-4`}
                style={{ zIndex: 10 }}
            >
                <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                    <div className="d-flex align-items-center">
                        <span className="fs-6 fw-semibold text-secondary me-3">Status:</span>
                        <Badge style={getManifestStatusBadgeStyle(manifestDetails.rawStatus)}>
                            {manifestDetails.status}
                        </Badge>
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                        {enableLodgeStatusButtons ? (
                            <>
                                {String(manifest?.status ?? "").toLowerCase() === "open" ? (
                                    <>
                                        {manifestWriteDisabledBecauseOffline ? (
                                            <span className="d-inline-block" title={manifestWriteDisabledTooltip}>
                                                <Button variant="outline" size="sm" disabled>
                                                    Close
                                                </Button>
                                            </span>
                                        ) : (
                                            <Button variant="outline" size="sm" onClick={onCloseManifestClick}>Close</Button>
                                        )}
                                        {manifestSendDisabledBecauseOffline ? (
                                            <span className="d-inline-block" title={manifestSendDisabledTooltip}>
                                                <Button variant="outline" size="sm" disabled>
                                                    Close and Ship
                                                </Button>
                                            </span>
                                        ) : (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openActionModal({
                                                    title: "Close and Ship Manifest",
                                                    body: "The manifest will be emailed to St Jeans and will not be able to be reopened for editing. Do you want to continue?",
                                                    primaryText: "Continue",
                                                    onPrimary: async () => {
                                                        if (!ensureOnlineOrToast()) return
                                                        await shipManifestAndUpdate()
                                                        refetchOrdersList()
                                                    },
                                                })}
                                            >
                                                Close and Ship
                                            </Button>
                                        )}
                                    </>
                                ) : null}
                                {String(manifest?.status ?? "").toLowerCase() === "closed" ? (
                                    <>
                                        {manifestWriteDisabledBecauseOffline ? (
                                            <span className="d-inline-block" title={manifestWriteDisabledTooltip}>
                                                <Button variant="outline" size="sm" disabled>
                                                    Re-open
                                                </Button>
                                            </span>
                                        ) : (
                                            <Button variant="outline" size="sm" onClick={onReopenManifestClick}>Re-open</Button>
                                        )}
                                        {manifestSendDisabledBecauseOffline ? (
                                            <span className="d-inline-block" title={manifestSendDisabledTooltip}>
                                                <Button variant="outline" size="sm" disabled>
                                                    Ship
                                                </Button>
                                            </span>
                                        ) : (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openActionModal({
                                                    title: "Ship Manifest",
                                                    body: "The manifest will be sent to St Jeans and will not be able to be reopened for editing. Do you want to continue?",
                                                    primaryText: "Continue",
                                                    onPrimary: async () => {
                                                        if (!ensureOnlineOrToast()) return
                                                        await shipManifestAndUpdate()
                                                        refetchOrdersList()
                                                    },
                                                })}
                                            >
                                                Ship
                                            </Button>
                                        )}
                                    </>
                                ) : null}
                                {String(manifest?.status ?? "").toLowerCase() === "shipped" ? (
                                    <>
                                        <Button variant="outline" size="sm" onClick={() => setRenderManifestPdf(true)}>View / Print Manifest</Button>
                                        {manifestSendDisabledBecauseOffline ? (
                                            <span className="d-inline-block" title={manifestSendDisabledTooltip}>
                                                <Button variant="outline" size="sm" disabled>
                                                    Resend Manifest
                                                </Button>
                                            </span>
                                        ) : (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openActionModal({
                                                    title: "Resend Manifest",
                                                    body: "The manifest will be emailed to St Jeans. Do you want to continue?",
                                                    primaryText: "Continue",
                                                    onPrimary: async () => {
                                                        if (!ensureOnlineOrToast()) return
                                                        await resendManifestEmail()
                                                        showActionSuccessToast("Manifest successfully sent to St Jeans")
                                                    },
                                                })}
                                            >
                                                Resend Manifest
                                            </Button>
                                        )}
                                    </>
                                ) : null}
                                {showPrintBoxLabelsButton ? (
                                    <Button variant="outline" size="sm" icon={<Printer size={16} />} onClick={onPrintBoxLabelsClick}>
                                        Print Box Labels
                                    </Button>
                                ) : null}
                            </>
                        ) : (
                            <>
                                {showPrintManifestButton && showManifestPrintActions ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        icon={<Printer size={16} />}
                                        className="v2-not-implemented-btn"
                                        onClick={() => showNotImplementedToast()}
                                        title="Not implemented yet"
                                    >
                                        View / Print Manifest
                                    </Button>
                                ) : null}
                                {showPrintLabelButton && showManifestPrintActions ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        icon={<Printer size={16} />}
                                        className="v2-not-implemented-btn"
                                        onClick={() => showNotImplementedToast()}
                                        title="Not implemented yet"
                                    >
                                        View / Print Label
                                    </Button>
                                ) : null}
                                {showPrintBoxLabelsButton ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        icon={<Printer size={16} />}
                                        onClick={onPrintBoxLabelsClick}
                                    >
                                        Print Box Labels
                                    </Button>
                                ) : null}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded shadow-sm p-4 mb-4">
                <div className="d-flex flex-column flex-lg-row gap-4">
                    <div
                        style={{
                            flex: "1 1 0",
                            minWidth: 0,
                            maxWidth: `${MANIFEST_DETAILS_COLUMN_MAX_WIDTH_PX}px`,
                        }}
                    >
                        <h3 className="fw-semibold text-secondary mb-3"
                            style={{ fontSize: "0.875rem" }}>
                            Manifest Details
                        </h3>
                        <div className="d-flex flex-column gap-3">
                            {detailsFields.map((field) => (
                                <div
                                    key={field.key}
                                    className=""
                                    style={MANIFEST_DETAILS_PAIR_STYLE}
                                >
                                    <span className="text-secondary" style={{ fontSize: "0.875rem" }}>
                                        {field.label}:
                                    </span>
                                    <span
                                        className="fw-semibold"
                                        style={{ fontSize: "0.875rem", ...MANIFEST_DETAILS_PAIR_VALUE_STYLE }}
                                    >
                                        {manifestDetails[field.key] ?? "—"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {showAdditionalInformation ? (
                        <div
                            style={{
                                flex: "1 1 0",
                                minWidth: 0,
                                maxWidth: `${MANIFEST_DETAILS_COLUMN_MAX_WIDTH_PX}px`,
                            }}
                        >
                            <h3 className="fw-semibold text-secondary mb-3"
                                style={{ fontSize: "0.875rem" }}>
                                Additional Information
                            </h3>
                            <div className="d-flex flex-column gap-3">
                                <div className="" style={MANIFEST_DETAILS_PAIR_STYLE}>
                                    <span className="text-secondary" style={{ fontSize: "0.875rem" }}>
                                        Label Description:
                                    </span>
                                    <span
                                        className="fw-semibold"
                                        style={{ fontSize: "0.875rem", ...MANIFEST_DETAILS_PAIR_VALUE_STYLE }}
                                    >
                                        {manifestDetails.labelDescription}
                                    </span>
                                </div>
                                <div className="" style={MANIFEST_DETAILS_PAIR_STYLE}>
                                    <span className="text-secondary" style={{ fontSize: "0.875rem" }}>
                                        Synced to Minotaur:
                                    </span>
                                    <span className="fw-semibold d-flex align-items-center"
                                        style={{ fontSize: "0.875rem", ...MANIFEST_DETAILS_PAIR_VALUE_STYLE }}>
                                        {manifestDetails.syncedToMinotaur ? (
                                            <CheckCircle2 size={16} className="text-success me-1" />
                                        ) : null}
                                        {manifestDetails.syncedToMinotaur ? "Yes" : "No"}
                                    </span>
                                </div>
                                <div className="" style={MANIFEST_DETAILS_PAIR_STYLE}>
                                    <span className="text-secondary" style={{ fontSize: "0.875rem" }}>
                                        VVBN Reference #:
                                    </span>
                                    <span
                                        className="fw-semibold"
                                        style={{ fontSize: "0.875rem", ...MANIFEST_DETAILS_PAIR_VALUE_STYLE }}
                                    >
                                        {manifestDetails.vvbnReference}
                                    </span>
                                </div>
                                <div className="" style={MANIFEST_DETAILS_PAIR_STYLE}>
                                    <span className="text-secondary" style={{ fontSize: "0.875rem" }}>
                                        Current Location:
                                    </span>
                                    <span
                                        className="fw-semibold"
                                        style={{ fontSize: "0.875rem", ...MANIFEST_DETAILS_PAIR_VALUE_STYLE }}
                                    >
                                        {manifestDetails.currentLocation}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            {!isMobile ? (
                <div className="bg-white rounded shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-bottom">
                        <h2 className="fs-5 fw-semibold text-dark mb-0">Orders</h2>
                    </div>
                    <OrdersDesktopTable
                        orders={orders}
                        totalRows={orders.length}
                        page={1}
                        perPage={Math.max(1, orders.length)}
                        sortBy={getSortBy()}
                        sortDirection={getSortDirection()}
                        onSort={handleSort}
                        onOrderClick={handleOrderClick}
                        showPagination={false}
                        showRefetchLoader={!isMobile && showRefetchLoader}
                        enableSelection={false}
                        columns={orderColumns}
                    />
                </div>
            ) : (
                <>
                    <div className="mb-3">
                        <p className="text-secondary mb-0" style={{ fontSize: "0.875rem" }}>
                            Showing {orders.length} orders
                        </p>
                    </div>
                    <OrdersMobileList
                        orders={orders}
                        onOrderClick={handleOrderClick}
                        enableInfiniteScroll={false}
                    />
                </>
            )}
            <ScrollToTopButton className="d-lg-none" />
        </V2PageContainer>
    )
}

